import { ApiProduct } from '@vend/types'
import { trackCustomHeapEvent } from '@vend/utilities'
import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../../../state/hooks'
import { selectCurrentSale } from '../../../../../../state/slices/currentSale/currentSaleSlice'
import {
  HEAP_LINE_ITEM_FULFILMENT_DECISION_ACTION,
  HEAP_PACKAGING_BREAKDOWN_OPTION_OBJECT,
} from '../../../../sale-model.constant'
import { useCrossLocationFulfilmentGate } from '../../OutletSelect'
import { ShouldFulfillOpt } from '../LineItemFulfillmentContext'

export type LineItemFulfilmentDecisionViewProps = {
  decision: ShouldFulfillOpt
  productId?: string
  productQuantity: number
  productName: string
  onSelect: (opt: ShouldFulfillOpt, isBreakdownSelected: boolean) => void
  onOutletSelect?: (outletId: string | undefined) => void
  initialSelectedOutletId?: string
  canShowBreakdownOption?: boolean
  outerProduct?: ApiProduct
  saleHasServiceOrder?: boolean
}

// Internal enum for display purposes
export enum DisplayOption {
  CONTINUE_ANYWAY = 'continue_anyway',
  BREAKDOWN = 'break_down',
  FULFIL_LATER = 'fulfil_later',
}

export const useLineItemFulfilmentDecisionViewModel = ({
  decision,
  productQuantity,
  productName,
  onSelect,
  onOutletSelect = () => {},
  canShowBreakdownOption = false,
  outerProduct,
}: LineItemFulfilmentDecisionViewProps) => {
  // LRX-38473: On layby sales the cashier must not be able to change the
  // fulfilment type when adding an out-of-stock product. The stock warning is
  // still shown so they can choose to keep ("Continue anyway") or remove the
  // item. The "Fulfill later" path that exposes Delivery/Pickup is kept in the
  // layout but disabled with a tooltip explaining why.
  const currentSale = useAppSelector(selectCurrentSale)
  const isLaybySale = !!currentSale.attributes?.includes('layby')

  const initialDisplayOption =
    decision === ShouldFulfillOpt.YES && !isLaybySale
      ? DisplayOption.FULFIL_LATER
      : DisplayOption.CONTINUE_ANYWAY

  const [
    selectedDisplayOption,
    setSelectedDisplayOption,
  ] = useState<DisplayOption>(initialDisplayOption)

  const {
    canUseCrossLocationFulfilment,
    hasPermission: hasCLFOutletPermission,
  } = useCrossLocationFulfilmentGate()

  const shouldShowOutletSelect =
    canUseCrossLocationFulfilment &&
    selectedDisplayOption === DisplayOption.FULFIL_LATER &&
    !isLaybySale

  useEffect(() => {
    if (!shouldShowOutletSelect) {
      onOutletSelect(undefined)
    }
  }, [onOutletSelect, shouldShowOutletSelect])

  const handleOptionSelect = (displayOption: DisplayOption) => {
    setSelectedDisplayOption(displayOption)

    onSelect(
      displayOption === DisplayOption.FULFIL_LATER
        ? ShouldFulfillOpt.YES
        : ShouldFulfillOpt.NO,
      displayOption === DisplayOption.BREAKDOWN
    )
  }

  useEffect(() => {
    if (canShowBreakdownOption) {
      trackCustomHeapEvent(
        {
          action: HEAP_LINE_ITEM_FULFILMENT_DECISION_ACTION,
          object: HEAP_PACKAGING_BREAKDOWN_OPTION_OBJECT,
        },
        {
          productName,
          outerProductName: outerProduct?.name || '',
          productQuantity,
        }
      )
    }
    // intentionally omit dependencies to track this event only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isLaybySale,
    selectedDisplayOption,
    hasCLFOutletPermission,
    shouldShowOutletSelect,
    handleOptionSelect,
    handleOutletSelect: onOutletSelect,
  }
}
